import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const OtherExpenses = () => {
  const { showSuccess, showError } = useToast();
  const [expenses, setExpenses] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Form
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [particular, setParticular] = useState('');
  const [activity, setActivity] = useState('');
  const [payType, setPayType] = useState('Cash');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/expenses');
      if (res.success) {
        setExpenses(res.data || []);
        setTotalAmount(res.totalAmount || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!particular || !amount) {
      showError('Please fill particulars and amount.');
      return;
    }

    setSaving(true);
    try {
      await axiosClient.post('/expenses', {
        date,
        particular,
        activity,
        payType,
        amount: parseFloat(amount)
      });

      showSuccess(`Expense voucher of ₹${amount} saved successfully!`);
      setParticular('');
      setActivity('');
      setAmount('');
      fetchExpenses();
    } catch (err) {
      showError(err.message || 'Error saving expense.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this expense voucher?')) {
      try {
        await axiosClient.delete(`/expenses/${id}`);
        showSuccess('Expense voucher deleted successfully.');
        fetchExpenses();
      } catch (err) {
        showError(err.message || 'Error deleting expense.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-wallet2 text-primary me-2"></i> Other Daily Expenses
          </h3>
          <p className="text-muted small mb-0">Record and monitor shop operating costs, tea, transport, repairs, and utilities</p>
        </div>
        <div className="badge bg-danger fs-6 p-2">
          Total Expenses: ₹{parseFloat(totalAmount).toLocaleString('en-IN')}
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light py-2 fw-semibold">New Expense Voucher</div>
            <div className="card-body p-3">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small text-muted">Expense Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small text-muted">Particulars (Description) *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Shop Electricity Bill, Ice Delivery, Staff Refreshments"
                    value={particular}
                    onChange={(e) => setParticular(e.target.value)}
                    required
                  />
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small text-muted">Payment Type</label>
                    <select
                      className="form-select"
                      value={payType}
                      onChange={(e) => setPayType(e.target.value)}
                    >
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI / Online</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">Amount (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control fw-bold text-danger text-end"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small text-muted">Activity / Category</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Utilities, Logistics, Maintenance"
                    value={activity}
                    onChange={(e) => setActivity(e.target.value)}
                  />
                </div>

                <button type="submit" className="btn btn-ocean w-100" disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check-circle me-1"></i>}
                  Save Voucher
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white py-3">
              <h5 className="fw-bold mb-0 text-dark">Recent Expense Vouchers</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th>Voucher #</th>
                      <th>Date</th>
                      <th>Particulars</th>
                      <th>Activity</th>
                      <th>Pay Type</th>
                      <th className="text-end">Amount (₹)</th>
                      <th className="text-center" style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                        </td>
                      </tr>
                    ) : expenses.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted">No expense vouchers recorded.</td>
                      </tr>
                    ) : (
                      expenses.map((e) => (
                        <tr key={e.Id}>
                          <td><strong>#{e.VoucherNo || e.Id}</strong></td>
                          <td>{formatDate(e.Date)}</td>
                          <td className="fw-bold text-dark">{e.particular}</td>
                          <td><span className="badge bg-light text-dark border">{e.Activity || 'General'}</span></td>
                          <td>{e.payType}</td>
                          <td className="text-end fw-bold text-danger">₹{parseFloat(e.Amount).toFixed(2)}</td>
                          <td className="text-center">
                            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDelete(e.Id)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherExpenses;
